import { useMemo } from 'react';
import { AppHeader, ProgressBar } from '../components/common';
import { useT } from '../hooks/useT';
import { computeStats } from '../services/progress';
import { useBank } from '../state/BankContext';
import { useProgress } from '../state/ProgressContext';
import { useRouter } from '../state/RouterContext';
import { useSettings } from '../state/SettingsContext';

function Stat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="stat">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}

/** The learner-facing progress page: simple bars, no dense charts. */
export function ProgressScreen() {
  const t = useT();
  const { questions, signs } = useBank();
  const { progress } = useProgress();
  const { settings } = useSettings();
  const { navigate } = useRouter();

  const stats = useMemo(() => computeStats(progress, questions, signs), [progress, questions, signs]);

  return (
    <>
      <AppHeader title={t('myProgress')} />
      <main id="main" className="app-main stack">
        <section className="card stack">
          <h2 className="card__title">{t('questionsLearned')}</h2>
          <ProgressBar value={stats.learned} max={stats.totalQuestions} tone="success" />
          <h2 className="card__title" style={{ marginTop: 'var(--space-3)' }}>
            {t('signsLearned')}
          </h2>
          <ProgressBar value={stats.signsLearned} max={stats.totalSigns} tone="success" />
        </section>

        <section className="stat-grid">
          <Stat value={stats.totalQuestions} label={t('totalQuestions')} />
          <Stat value={stats.seen} label={t('questionsSeen')} />
          <Stat value={stats.learned} label={t('questionsLearned')} />
          <Stat value={stats.needsPractice} label={t('questionsNeedPractice')} />
          <Stat value={stats.testsTaken} label={t('testsTaken')} />
          <Stat value={stats.testsPassed} label={t('testsPassed')} />
        </section>

        {stats.needsPractice > 0 ? (
          <button
            type="button"
            className="btn btn--primary btn--big btn--block"
            onClick={() => navigate({ name: 'quiz', mode: 'mistakes' })}
          >
            <span className="btn__icon" aria-hidden="true">
              🔁
            </span>
            {t('myMistakes')}
          </button>
        ) : null}

        {!settings.easyMode ? (
          <button type="button" className="btn btn--block" onClick={() => navigate({ name: 'caregiver' })}>
            <span className="btn__icon" aria-hidden="true">
              👨‍👩‍👦
            </span>
            {t('caregiver')}
          </button>
        ) : null}
      </main>
    </>
  );
}
